import { useEffect, useRef, useState } from "react";
import { ICON_BASE_PATH } from "@/app/utils/config";
import "./GxDropdownSmall.scss";

interface GxDropdownSmallProps {
  id?: string;
  keyName?: string | number;
  data: any[];
  name: string;
  option: string;
  value?: string | number | null;
  onChange: (value: string | number) => void;
  placeholder?: string;
  disabled?: boolean;
  onDisabledClick?: () => void;
}

export default function GxDropdownSmall({
  id = "gx-dropdown-small",
  keyName,
  data,
  name,
  option,
  value,
  onChange,
  disabled,
  placeholder = "Select",
  onDisabledClick,
}: Readonly<GxDropdownSmallProps>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const listboxId = `${id}-listbox`;

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [open]);

  const selectedLabel = data?.find((item) => item[option] === value)?.[name];

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (disabled) {
      onDisabledClick?.();
      return;
    }

    setOpen((prev) => !prev);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        setOpen((prev) => !prev);
        break;

      case "Escape":
        setOpen(false);
        break;

      case "ArrowDown":
        e.preventDefault();
        setOpen(true);
        break;
    }
  };

  const handleOptionClick = (optionValue: string | number) => {
    onChange(optionValue);
    setOpen(false);
  };

  return (
    <div className="gx-dropdown-small" ref={ref}>
      <button
        className={`gx-dropdown-small-control ${
          disabled ? "gx-dropdown-small-control-disable" : ""
        }`}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        disabled={false}
      >
        <span
          className={`gx-dropdown-small-value ${
            selectedLabel ? "" : "gx-dropdown-small-placeholder"
          }`}
        >
          {selectedLabel || placeholder}
        </span>

        <img
          src={`${ICON_BASE_PATH}down_arrow1.svg`}
          alt=""
          aria-hidden="true"
        />
      </button>

      {!disabled && open && (
        <ul id={listboxId} role="listbox" className="gx-dropdown-small-menu">
          {data?.length > 0 ? (
            data.map((opt, i) => {
              const optionValue = opt[option];
              const isSelected = optionValue === value;

              return (
                <li
                  key={keyName ? opt[keyName] : i}
                  role="option"
                  aria-selected={isSelected}
                >
                  <button
                    className={
                      opt[option] === value
                        ? "gx-dropdown-small-selected-option gx-p14-md"
                        : "gx-dropdown-small-option gx-p14-md"
                    }
                    onClick={() => handleOptionClick(opt[option])}
                  >
                    {opt[name]}
                  </button>
                </li>
              );
            })
          ) : (
            <li className="gx-dropdown-small-option gx-p14-md">
              No data available
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
